<!-- app-subdomain-creators\src\Components\CreatorAssignMemberNoteModal.vue -->

<template>
  <TransitionRoot as="template" :show="isOpen">
    <Dialog class="relative z-50" @close="handleClose">
      <!-- Overlay -->
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-75 transition-opacity backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed top-[20%] inset-x-0 z-10 w-screen">
        <div
          class="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform rounded-lg bg-white px-6 pb-6 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-8"
            >
              <!-- Close X -->
              <button
                type="button"
                class="absolute top-4 right-4 rounded-full text-zinc-400 hover:text-zinc-600 focus:outline-none"
                @click="handleClose"
              >
                <XMarkIcon class="h-5 w-5" />
              </button>

              <!-- Header -->
              <div class="sm:flex sm:items-start">
                <div
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10"
                >
                  <PencilSquareIcon class="h-6 w-6 text-yellow-500" aria-hidden="true" />
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle
                    as="h3"
                    class="text-xl font-semibold leading-6 text-gray-900"
                  >
                    {{ mode === 'edit' ? 'Edit note' : 'Add a note' }}
                  </DialogTitle>
                  <p class="mt-1 text-sm text-gray-500">
                    Internal note
                    <span v-if="memberName">
                      for<br />
                      {{ memberName }}
                    </span>
                  </p>
                </div>
              </div>

              <!-- Body -->
              <div class="mt-6 space-y-3">
                <div class="flex items-center justify-between">
                  <label class="block text-sm font-medium text-gray-700">
                    Note
                  </label>
                  <span class="text-xs text-zinc-500">
                    {{ noteText.length }}/100
                  </span>
                </div>

                <textarea
                  v-model="noteText"
                  rows="3"
                  maxlength="100"
                  @input="handleInput"
                  class="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-[#2D8CFF] focus:outline-none focus:ring-1 focus:ring-[#2D8CFF]"
                  placeholder="Add an internal note (max 100 characters, up to 2 lines)..."
                ></textarea>

                <p class="text-[11px] text-gray-400">
                  Max 100 characters, up to 2 lines. Notes are only visible to you and your team.
                </p>

                <p
                  v-if="localError"
                  class="text-xs text-red-600"
                >
                  {{ localError }}
                </p>
              </div>

              <!-- Footer buttons -->
              <div class="mt-8 sm:mt-10 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  class="w-full justify-center rounded-md bg-[#2D8CFF] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#1b6ad1] sm:ml-3 sm:w-auto disabled:bg-[#2D8CFF]/20 disabled:text-white disabled:cursor-not-allowed"
                  :disabled="!canSave"
                  @click="handleSave"
                >
                  {{ mode === 'edit' ? 'Save changes' : 'Add note' }}
                </button>
                <button
                  type="button"
                  class="mt-8 w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  @click="handleClose"
                >
                  Cancel
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import {
  XMarkIcon,
  PencilSquareIcon,
} from '@heroicons/vue/24/outline'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  memberName: {
    type: String,
    default: '',
  },
  // 'create' | 'edit'
  mode: {
    type: String,
    default: 'create',
  },
  // pre-fill when editing
  initialNoteText: {
    type: String,
    default: '',
  },
  // used only in edit mode
  noteId: {
    type: [Number, String, null],
    default: null,
  },
})

const emit = defineEmits(['close', 'save'])

const noteText = ref('')
const localError = ref('')

const canSave = computed(() => {
  return noteText.value.trim().length > 0
})

// Reset when modal opens
watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      noteText.value = (props.initialNoteText || '').slice(0, 100)
      localError.value = ''
    }
  }
)

function handleClose() {
  emit('close')
}

/**
 * Anti-spam newline limiter: max 2 lines.
 * Same pattern as your BTC note section.
 */
function limitNewlines(raw) {
  let val = raw ?? ''
  const parts = val.split('\n')

  if (parts.length > 2) {
    val = parts[0] + '\n' + parts[1]
  }

  return val
}

function handleInput(e) {
  const raw = e.target.value
  const limited = limitNewlines(raw)
  // extra safety: enforce max 100 chars even if maxlength is bypassed
  noteText.value = limited.slice(0, 100)
}

function handleSave() {
  localError.value = ''

  const trimmed = noteText.value.trim()
  if (!trimmed) {
    localError.value = 'Please enter a note before saving.'
    return
  }

  emit('save', {
    mode: props.mode === 'edit' ? 'edit' : 'create',
    noteId: props.noteId ?? null,
    noteText: trimmed,
  })
}
</script>
